import BaseComponent from './BaseComponent';
import MenuDrawer from './MenuDrawer';
import { gsap } from 'gsap';
import { getKeyboardFocusableElements } from '../utils';

export default class Menu extends BaseComponent {
	constructor(elm, header) {
		super();

		this.header = header;
		this.breakpoint = window.matchMedia('(min-width: 992px)');
		this.reduceMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
		this.isOpen = false;

		// Elements
		this.elm = elm;
		this.background = this.header.elm.querySelector('[data-menu-background]');
		this.items = this.elm.querySelectorAll('[data-menu-item]');
		this.links = this.elm.querySelectorAll('a, button');

		this.drawerElms = this.header.elm.querySelectorAll('[data-drawer]');
		this.drawers = [];
		this.drawerElms.forEach((drawer) => {
			this.drawers.push(new MenuDrawer(drawer, this));
		});

		this.setInitialState();

		// Listeners
		this.breakpoint.addEventListener('change', () => {
			this.setInitialState();
		});

		this.headerLogo = this.header.elm.querySelector('[data-header-logo]');
		this.headerLogo.addEventListener('click', (e) => {
			this.cleanupLinks();
		});

		this.links.forEach((link) => {
			link.addEventListener('click', (e) => {
				this.cleanupLinks();
				link.closest('.c-menu-item').classList.add('is-active');
			});
		});
	}

	cleanupLinks() {
		this.links.forEach((link) => {
			link.closest('.c-menu-item').classList.remove('is-active');
		});
	}

	setInitialState() {
		this.header.menuButton.innerHTML = 'menu';
		if (!this.breakpoint.matches) {
			gsap.set(this.elm, {
				autoAlpha: 1,
				scale: 1,
			});
			gsap.set(this.background, {
				scaleY: 0,
			});
			gsap.set(this.items, {
				autoAlpha: 0,
				scale: 0.9,
			});
		} else {
			gsap.set(this.elm, {
				autoAlpha: 1,
				scale: 1,
			});
			gsap.set(this.background, {
				scaleY: 1,
			});
			gsap.set(this.items, {
				autoAlpha: 1,
				scale: 1,
			});
		}
	}

	toggle() {
		if (this.isOpen) {
			this.close();
		} else {
			this.open();
		}
	}

	open() {
		if (!this.breakpoint.matches) {
			this.elm.classList.add('is-open');
			this.isOpen = true;
			this.header.stay = true;
			this.header.menuButton.innerHTML = 'sluiten';
			this.header.menuButton.setAttribute('aria-expanded', 'true');

			gsap.to(this.background, {
				scaleY: 1,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.6,
				ease: 'sine.out',
			});

			gsap.to(this.items, {
				autoAlpha: 1,
				scale: 1,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.6,
				delay: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
				ease: 'power2.out',
				stagger: {
					amount: this.reduceMotionMediaQuery.matches ? 0 : 0.2,
					ease: 'power2.out',
				},
			});

			this.inertElements();
			this.trapFocus(this.elm, this.header.menuButton);
		}
	}

	close() {
		this.isOpen = false;
		this.header.stay = false;
		this.header.menuButton.innerHTML = 'menu';
		this.header.menuButton.setAttribute('aria-expanded', 'false');

		this.drawers.forEach((drawer) => {
			drawer.closeMenu();
		});

		if (!this.breakpoint.matches) {
			gsap.to(this.elm, {
				autoAlpha: 0,
				scale: 0.9,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.6,
				ease: 'power2.out',
			});

			gsap.to(this.background, {
				scaleY: 0,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.6,
				ease: 'sine.inOut',
				onComplete: () => {
					this.elm.classList.remove('is-open');
					this.setInitialState();
				},
			});

			this.uninertElements();
			this.removeTrapFocus();
		}
	}

	openDrawer() {
		if (!this.breakpoint.matches) {
			gsap.to(this.elm, {
				scale: 0.8,
				autoAlpha: 0,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.6,
				ease: 'power2.inOut',
			});
		}
	}

	closeDrawer() {
		if (!this.breakpoint.matches) {
			gsap.to(this.elm, {
				scale: 1,
				autoAlpha: 1,
				duration: this.reduceMotionMediaQuery.matches ? 0 : 0.6,
				ease: 'power2.inOut',
			});
			if (this.isOpen) {
				this.trapFocus(this.elm);
				setTimeout(() => {
					this.firstFocusableEl.focus();
				}, 10);
			}
		}
	}

	setKeyboardFocusableElements(menu = this.elm) {
		this.keyboardFocusableElements = [];

		let focusableElements = [];
		if (this.breakpoint.matches) {
			focusableElements = getKeyboardFocusableElements(menu, '[data-element-desktop]');
		} else {
			focusableElements = getKeyboardFocusableElements(menu, '[data-element-mobile]');
		}

		focusableElements.forEach((item) => {
			this.keyboardFocusableElements.push(item);
		});

		this.firstFocusableEl = this.keyboardFocusableElements[0];
		this.lastFocusableEl = this.keyboardFocusableElements[this.keyboardFocusableElements.length - 1];
	}

	trapFocus(menu, button) {
		this.removeTrapFocus();

		this.tabfocussed = (event) => this.tabFocus(event, button);
		document.addEventListener('keydown', this.tabfocussed);

		this.setKeyboardFocusableElements(menu);
	}

	tabFocus(e, button) {
		if (e.key === 'Escape') {
			this.close();
			button.focus();
		}

		const isTabPressed = e.key === 'Tab';
		if (!isTabPressed) {
			return;
		}

		if (e.shiftKey) {
			if (document.activeElement === this.firstFocusableEl) {
				e.preventDefault();
				this.lastFocusableEl.focus();
			}
		} else {
			if (document.activeElement === button) {
				e.preventDefault();
				this.firstFocusableEl.focus();
			} else {
				if (document.activeElement === this.lastFocusableEl) {
					e.preventDefault();
					this.firstFocusableEl.focus();
				}
			}
		}
	}

	removeTrapFocus() {
		this.keyboardFocusableElements = [];
		document.removeEventListener('keydown', this.tabfocussed);
	}

	inertElements() {
		document.querySelector('[data-barba="container"]').setAttribute('inert', '');
	}

	uninertElements() {
		document.querySelector('[data-barba="container"]').removeAttribute('inert');
	}
}
